import { z } from 'zod';

import { BO_LIST_MAX_RECORDS } from '@Common/constants';
import { createZodJSONStringParsed } from '@Common/types/zod/zod-json-string';
import { brandParams } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const MAX_RANGE_ITEMS = 40000; // Tmp increase from 20k to 40k : https://sh4res.atlassian.net/browse/BRAV-10270

export function buildZodBoListQueryParsed<
  SortColumns extends string,
  ZodFilters extends z.ZodType<z.infer<ZodFilters>>,
>(
  zodFilters: ZodFilters,
  sortColumns: readonly SortColumns[],
  defaultValues?: {
    filter?: NoInfer<z.core.util.NoUndefined<z.output<ZodFilters>>>;
    range?: [from: number, to: number];
    sort?: [column: SortColumns, direction: 'ASC' | 'DESC'];
  },
) {
  // use array as we don't want to use zod tuple since openapi schema generation can't handle them
  const range = z
    .array(z.number().int().nonnegative())
    .length(2)
    .refine((arr) => arr[1] >= arr[0], { message: 'Incorrect range order' })
    .refine((arr) => arr[0] <= BO_LIST_MAX_RECORDS, {
      message: `Invalid range start, max boundary is ${BO_LIST_MAX_RECORDS}`,
    })
    .refine((arr) => arr[1] <= arr[0] + MAX_RANGE_ITEMS - 1, {
      message: `Invalid range length, max items is ${MAX_RANGE_ITEMS}, like '[0, ${
        MAX_RANGE_ITEMS - 1
      }]'`,
    })
    .pipe(
      z.custom<[from: number, to: number]>().meta({
        description: 'Range',
        override: { type: 'array' },
      }),
    );

  // use array as we don't want to use zod tuple since openapi schema generation can't handle them
  const sort = z
    .array(ZodTrimmedString)
    .length(2)
    .refine((arr) => sortColumns.includes(arr[0] as SortColumns), {
      message: 'Invalid sort column',
    })
    .refine((arr) => ['ASC', 'DESC'].includes(arr[1]), {
      message: 'Invalid sort direction',
    })
    .pipe(
      z.custom<[column: SortColumns, direction: 'ASC' | 'DESC']>().meta({
        description: 'Sort',
        example: '+14155552671',
        override: { type: 'array', format: 'sort' }, // custom override for OpenAPI output
      }),
    );

  const finalFilter = createZodJSONStringParsed(zodFilters);
  const finalRange = createZodJSONStringParsed(range);
  const finalSort = createZodJSONStringParsed(sort);

  return brandParams(
    z.object({
      filter: defaultValues?.filter
        ? finalFilter.default(defaultValues.filter).optional()
        : finalFilter.optional(),
      range: defaultValues?.range
        ? finalRange.default(defaultValues.range).optional()
        : finalRange.optional(),
      sort: defaultValues?.sort
        ? finalSort.default(defaultValues.sort).optional()
        : finalSort.optional(),
    }),
  );
}
